import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, Clock, Play, RotateCw, XCircle, RefreshCw} from 'lucide-react'
import {ErrorLogLink} from '@/components/ErrorLogLink'
import {ErrorText} from '@/components/shared/ErrorText'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAuth} from '@/contexts/auth-context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {toast} from '@/hooks/use-toast'
import {permissions} from '@/lib/permissions'
import {SmallText} from '@/components/shared/SmallText'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {Checkbox} from '@/components/ui/checkbox'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {ProvisioningTaskControllerTasksEndpoint} from '@/types/endpoint-contracts'
import {formatRelativeTime} from '@/lib/date-utils'
import {ProvisioningAction, ProvisioningTaskStatus} from '@/types/provisioning'
import type {
    BulkRetry,
    ProvisioningAction as ProvisioningActionType,
    ProvisioningTask,
    ProvisioningTaskStatus as ProvisioningTaskStatusType
} from '@/types/provisioning'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'

interface PageResponse<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export function ProvisioningTasks() {
    const services = useServices()
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    usePageTitle(i18n.provisioning.tasksPageTitle)

    const canManage = hasPermission(permissions.provisioning.manage)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ProvisioningTaskControllerTasksEndpoint,
        defaultSortBy: ProvisioningTaskControllerTasksEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const {data, isLoading, error, refetch} = useQuery<PageResponse<ProvisioningTask>>({
        queryKey: ['provisioning-tasks', queryString, page, pageSize],
        queryFn: () => services.provisioning.list(Object.fromEntries(new URLSearchParams(queryString))),
        refetchInterval: 10000,
    })

    const totalItems = data?.totalElements || 0
    const totalPages = data?.totalPages || 0

    const failedRows = useMemo(
        () => data?.content.filter((t) => t.status === ProvisioningTaskStatus.FAILED) ?? [],
        [data],
    )

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const bulkRetryDialog = useDialog()

    const allFailedSelected = failedRows.length > 0
        && failedRows.every((t) => selectedIds.has(t.id))
    const someSelected = selectedIds.size > 0 && !allFailedSelected

    const toggleRow = (id: string) => {
        const next = new Set(selectedIds)
        if (next.has(id)) {
            next.delete(id)
        } else {
            next.add(id)
        }
        setSelectedIds(next)
    }

    const toggleSelectAll = () => {
        if (allFailedSelected) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(failedRows.map((t) => t.id)))
        }
    }

    const targetSystemFilterId = (searchAndFilters.filters['TARGET_SYSTEM_ID'] as string | undefined) ?? undefined
    const filteredTargetSystemName = useMemo(() => {
        if (!targetSystemFilterId) return null
        const match = data?.content.find((t) => t.targetSystem.id === targetSystemFilterId)
        return match?.targetSystem.displayName ?? null
    }, [targetSystemFilterId, data])

    const bulkRetryMutation = useEntityMutation<BulkRetry, Error, string[]>({
        mutationFn: (taskIds: string[]) => services.provisioning.retryBulk(taskIds),
        invalidate: [['provisioning-tasks']],
        closeDialog: bulkRetryDialog.close,
        onSuccess: (result) => {
            setSelectedIds(new Set())
            toast({
                description: i18n.provisioning.bulkRetrySuccess({
                    retried: result.retried,
                    skipped: result.skipped.length,
                }),
            })
        },
    })

    const singleRetryMutation = useEntityMutation<unknown, Error, string>({
        mutationFn: (id: string) => services.provisioning.retry(id),
        invalidate: [['provisioning-tasks']],
        successMessage: i18n.provisioning.retryQueued,
    })

    const getStatusBadge = (status: ProvisioningTaskStatusType) => {
        switch (status) {
            case ProvisioningTaskStatus.RUNNING:
                return <Badge variant={BadgeVariant.SUCCESS}><Icon icon={Play} size="xs" className="mr-1"/>{i18n.provisioning.running}</Badge>
            case ProvisioningTaskStatus.FAILED:
                return <Badge variant="destructive"><Icon icon={XCircle} size="xs" className="mr-1"/>{i18n.provisioning.failed}
                </Badge>
            case ProvisioningTaskStatus.QUEUED:
                return <Badge variant="outline"><Icon icon={Clock} size="xs" className="mr-1"/>{i18n.provisioning.queued}</Badge>
            case ProvisioningTaskStatus.DONE:
                return <Badge variant={BadgeVariant.SUCCESS}><Icon icon={CheckCircle} size="xs" className="mr-1"/>{i18n.provisioning.done}</Badge>
            case ProvisioningTaskStatus.CANCELED:
                return <Badge variant="outline"><Icon icon={XCircle} size="xs" className="mr-1"/>{i18n.provisioning.canceled}</Badge>
        }
    }

    const getActionBadge = (action: ProvisioningActionType) => {
        switch (action) {
            case ProvisioningAction.GRANT:
                return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionGrant}</Badge>
            case ProvisioningAction.REVOKE:
                return <Badge variant="outline">{i18n.provisioning.actionRevoke}</Badge>
            case ProvisioningAction.CREATE_ACCOUNT:
                return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionCreateAccount}</Badge>
            case ProvisioningAction.ENABLE_ACCOUNT:
                return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionEnableAccount}</Badge>
            case ProvisioningAction.DISABLE_ACCOUNT:
                return <Badge variant="warning">{i18n.provisioning.actionDisableAccount}</Badge>
            case ProvisioningAction.DELETE_ACCOUNT:
                return <Badge variant="destructive">{i18n.provisioning.actionDeleteAccount}</Badge>
            case ProvisioningAction.UPDATE_ACCOUNT:
                return <Badge variant="secondary">{i18n.provisioning.actionUpdateAccount}</Badge>
        }
    }

    const bulkActionBar = canManage && selectedIds.size > 0 ? (
        <InlineRow gap="sm" align="center">
            <MutedText>{i18n.provisioning.bulkRetrySelected({count: selectedIds.size})}</MutedText>
            <Button
                data-testid="btn-bulk-retry"
                variant="destructive"
                size="sm"
                onClick={() => bulkRetryDialog.open()}
            >
                <Icon icon={RotateCw} size="xs" className="mr-1"/>
                {i18n.provisioning.bulkRetrySelected({count: selectedIds.size})}
            </Button>
        </InlineRow>
    ) : undefined

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.provisioningTask}
            loading={isLoading}
            error={error}
            title={i18n.provisioning.activeTasks}
            count={totalItems}
            description={`${i18n.provisioning.taskCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.provisioning.matchingFilters}` : ''}`}
            data-testid="provisioning-tasks-list"
            actions={bulkActionBar}
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
            filters={
                <InlineRow gap="sm" align="center" className="w-full">
                    <UnifiedSearchBar
                        contract={ProvisioningTaskControllerTasksEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.provisioning.filterPlaceholder}
                    />
                    <Button data-testid="btn-refresh" variant="outline" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="h-4 w-4"/>
                    </Button>
                </InlineRow>
            }
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        {canManage && (
                            <TableHead data-testid="col-select" className="w-10">
                                <Checkbox
                                    data-testid="check-select-all"
                                    checked={allFailedSelected ? true : someSelected ? 'indeterminate' : false}
                                    onCheckedChange={toggleSelectAll}
                                    disabled={failedRows.length === 0}
                                    aria-label={i18n.provisioning.selectAll}
                                />
                            </TableHead>
                        )}
                        <TableHead data-testid="col-identity">{i18n.provisioning.identity}</TableHead>
                        <TableHead data-testid="col-account">{i18n.provisioning.account}</TableHead>
                        <TableHead data-testid="col-entitlement">{i18n.provisioning.entitlement}</TableHead>
                        <TableHead data-testid="col-target-system">{i18n.provisioning.targetSystem}</TableHead>
                        <TableHead data-testid="col-action">{i18n.provisioning.action}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.provisioning.createdAt}</TableHead>
                        <TableHead data-testid="col-retries">{i18n.provisioning.retries}</TableHead>
                        {canManage && <TableHead data-testid="col-actions" className="w-10"/>}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data?.content.map((task) => {
                        const isFailed = task.status === ProvisioningTaskStatus.FAILED
                        return (
                            <ClickableTableRow
                                key={task.id}
                                to={routes.provisioning.tasks.detail(task.id)}
                                data-testid={`task-row-${task.id}`}
                            >
                                {canManage && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            data-testid={`check-row-${task.id}`}
                                            checked={selectedIds.has(task.id)}
                                            onCheckedChange={() => toggleRow(task.id)}
                                            disabled={!isFailed}
                                            aria-label={i18n.provisioning.selectFailedTasks}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <EntityLinkChip
                                        value={task.identity}
                                        testId={`link-identity-detail-${task.identity.id}`}
                                        stopPropagation
                                    />
                                </TableCell>
                                <TableCell>
                                    <MutedText>
                                        {task.accountExternalIdentifier || '—'}
                                    </MutedText>
                                </TableCell>
                                <TruncatedCell maxWidth="200px" title={task.enforcementObject?.displayName || '—'}>
                                    {task.enforcementObject ? (
                                        <EntityLinkChip value={task.enforcementObject} stopPropagation/>
                                    ) : '—'}
                                </TruncatedCell>
                                <TableCell>
                                    <EntityLinkChip value={task.targetSystem} stopPropagation/>
                                </TableCell>
                                <TableCell>{getActionBadge(task.action)}</TableCell>
                                <TableCell>
                                    <Stack gap="xs">
                                        {getStatusBadge(task.status)}
                                        {task.error && (
                                            <InlineRow gap="xs">
                                                <ErrorText size="xs" truncate className="max-w-[200px]" title={task.error}>
                                                    {task.error}
                                                </ErrorText>
                                                <ErrorLogLink errorLogRef={task.errorLogRef} sourceType="ProvisioningTask" sourceId={task.id}/>
                                            </InlineRow>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <SmallText>
                                        {formatRelativeTime(task.createdAt)}
                                    </SmallText>
                                    {task.nextRetryAt && (
                                        <MutedText size="xs">
                                            {i18n.provisioning.retry} {formatRelativeTime(task.nextRetryAt)}
                                        </MutedText>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <MutedText size="sm">{task.retryCount}/{task.maxRetries}</MutedText>
                                </TableCell>
                                {canManage && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        {isFailed && (
                                            <Button
                                                data-testid={`btn-retry-row-${task.id}`}
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => singleRetryMutation.mutate(task.id)}
                                                disabled={singleRetryMutation.isPending}
                                                aria-label={i18n.provisioning.retrySingle}
                                                title={i18n.provisioning.retrySingle}
                                            >
                                                <Icon icon={RotateCw} size="xs"/>
                                            </Button>
                                        )}
                                    </TableCell>
                                )}
                            </ClickableTableRow>
                        )
                    })}
                    {data?.content.length === 0 && (
                        <TableEmptyState
                            colSpan={canManage ? 10 : 8}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.provisioning.noTasksMatch}
                            emptyMessage={i18n.provisioning.noActiveProvisioningTasks}
                        />
                    )}
                </TableBody>
            </Table>

            <Dialog open={bulkRetryDialog.isOpen} onOpenChange={(o) => o ? bulkRetryDialog.open() : bulkRetryDialog.close()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle data-testid="bulk-retry-dialog-title">
                            {i18n.provisioning.bulkRetryConfirmTitle({count: selectedIds.size})}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault()
                        bulkRetryMutation.mutate(Array.from(selectedIds))
                    }}>
                        <Stack>
                            <p>
                                <MutedText>
                                    {filteredTargetSystemName
                                        ? i18n.provisioning.bulkRetryConfirmBody({count: selectedIds.size, targetSystem: filteredTargetSystemName})
                                        : i18n.provisioning.bulkRetryConfirmBodyNoSystem({count: selectedIds.size})}
                                </MutedText>
                            </p>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="bulk-retry"
                            onCancel={bulkRetryDialog.close}
                            saving={bulkRetryMutation.isPending}
                            saveLabel={i18n.provisioning.bulkRetrySelected({count: selectedIds.size})}
                            savingLabel={i18n.provisioning.retrying}
                            destructive
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}
